import { HttpParams } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { EVERYDAY_SPEND_ENABLED } from '@features/everyday-spend/tokens/unleash-flags.token';
import { GiftCardApiService } from '@features/gift-card/services/gift-card-api/gift-card-api.service';
import { LoyaltyProgramApiService } from '@features/loyalty-program/services/loyalty-program-api/loyalty-program-api.service';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { ENV } from '@rd/core/tokens/rd-lib.token';
import { FeaturedPartner, FeaturedPartnerType } from '@rd/types/cms.type';
import { isNotNull } from '@rd/utils/isNotNull';
import { AnzEnvironmentType } from 'global-types';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

export interface AnzBenefit {
  buttonKey: string;
  buttonRedirectUrl: string;
  descriptionKey: string;
  headingKey: string;
  imageUrl: string;
  ariaLabel: string;
}

export interface AnzFeaturedPartner {
  ariaLabel: string;
  descriptionKey: string;
  headingKey: string;
  imageUrl?: string;
  linkKey: string;
  redirectUrl?: string | any[];
}

@Injectable()
export class AnzHomePageService {
  readonly isEverydaySpendEnabled = inject(EVERYDAY_SPEND_ENABLED);

  constructor(
    protected giftCardApiService: GiftCardApiService,
    protected loyaltyProgramApiService: LoyaltyProgramApiService,
    protected urlUtilsService: UrlUtilsService,
    protected cmsFacade: CmsFacade,
    @Inject(ENV) private env: AnzEnvironmentType,
  ) {}

  get benefits(): AnzBenefit[] {
    return [
      {
        headingKey: 'wl.anz.home_page.gift_cards.heading',
        descriptionKey: 'wl.anz.home_page.gift_cards.description',
        imageUrl: this.urlUtilsService.imageUrl('/images/giftcard-icon.png'),
        buttonKey: 'wl.anz.home_page.benefits.learn_more',
        ariaLabel: 'wl.anz.home_page.benefits.learn_more_gift_card_arial_label',
        buttonRedirectUrl: '/gift_card',
      },
      {
        headingKey: 'wl.anz.home_page.cashback.heading',
        descriptionKey: 'wl.anz.home_page.cashback.description',
        imageUrl: this.urlUtilsService.imageUrl('/images/cashback-icon.png'),
        buttonKey: 'wl.anz.home_page.benefits.learn_more',
        ariaLabel: 'wl.anz.home_page.benefits.learn_more_cashback_arial_label',
        buttonRedirectUrl: '/redeem_cash',
      },
      {
        headingKey: 'wl.anz.home_page.airline_partners.heading',
        descriptionKey: 'wl.anz.home_page.airline_partners.description',
        imageUrl: this.urlUtilsService.imageUrl(
          '/images/airline-partner-icon.png',
        ),
        buttonKey: 'wl.anz.home_page.benefits.learn_more',
        ariaLabel:
          'wl.anz.home_page.benefits.learn_more_airline_partners_arial_label',
        buttonRedirectUrl: '/transfer',
      },
      {
        ariaLabel:
          'wl.anz.home_page.benefits.learn_more_merchandise_arial_label',
        headingKey: 'wl.anz.home_page.merchandise.heading',
        descriptionKey: 'wl.anz.home_page.merchandise.description',
        imageUrl: this.urlUtilsService.imageUrl('/icons/icon-merchandise.svg'),
        buttonKey: 'wl.anz.home_page.benefits.learn_more',
        buttonRedirectUrl: '/merchandise',
      },
      ...(this.isEverydaySpendEnabled
        ? [
            {
              ariaLabel: 'Everyday Rewards learn more',
              headingKey: 'Everyday Rewards',
              descriptionKey:
                'Redeem your ANZ Reward Points for Everyday Rewards points.',
              imageUrl: this.urlUtilsService.imageUrl(
                '/icons/icon-everyday-spend.svg',
              ),
              buttonKey: 'wl.anz.home_page.benefits.learn_more',
              buttonRedirectUrl: '/everyday_rewards/checkout',
            },
          ]
        : []),
    ];
  }

  // If featured partner is GC, do a search to get the GC's ID and display the GC logo
  // If featured partner is TC, do an LP search to get the LP's ID and display that LP logo
  // If featured partner is Merch, then just do a "Merchandise listing page" search
  getFeaturedPartnerApiData(): Observable<Partial<AnzFeaturedPartner>[]> {
    return this.cmsFacade
      .getContentByName({
        pageName: 'featured-partners',
        pageType: 'featuredpartners',
        locale: 'en-au',
      })
      .pipe(
        filter(isNotNull),
        map((content) => {
          if (content.type === 'featuredpartners') {
            return content.items;
          }
          return [];
        }),
        // Filter out the featured partners that are not shown in the home page
        // so we don't need to make unnecessary API calls for those partners
        map((featuredPartners) =>
          featuredPartners.filter((featuredPartner) =>
            this.env === 'production'
              ? featuredPartner.showFeaturedPartner
              : featuredPartner.previewFeaturedPartner,
          ),
        ),
        switchMap((featuredPartners) => {
          const redirectUrls$ = featuredPartners.map((featuredPartner) => {
            switch (featuredPartner.type) {
              case FeaturedPartnerType.giftCard:
                return this.giftCardApiService
                  .getGiftCards(
                    new HttpParams().set(
                      'filter[name][$like]',
                      featuredPartner.partnerQueryString,
                    ),
                  )
                  .pipe(
                    map((results) => {
                      const gc = Array.isArray(results)
                        ? results[0]
                        : undefined;
                      return [`/gift_card/checkout/${gc?.id}`];
                    }),
                  );
              case FeaturedPartnerType.merchandise:
                return of([
                  '/merchandise/all',
                  {
                    searchKeyword:
                      featuredPartner.partnerQueryString.toLowerCase(),
                  },
                ]);
              case FeaturedPartnerType.loyaltyProgram:
                return this.loyaltyProgramApiService
                  .getLoyaltyPrograms(
                    new HttpParams().set(
                      'filter[transfer_connect_identifier][$eq]',
                      featuredPartner.partnerQueryString,
                    ),
                  )
                  .pipe(
                    map((results) => {
                      const lp = Array.isArray(results)
                        ? results[0]
                        : undefined;
                      return [`/transfer/checkout/${lp?.id}`];
                    }),
                  );
              case FeaturedPartnerType.retail: {
                return of(['/everyday_rewards/checkout']);
              }
              default: {
                return of([]);
              }
            }
          });

          return combineLatest([of(featuredPartners), ...redirectUrls$]);
        }),
        map((results) => {
          const featuredPartners = results[0] as Array<FeaturedPartner>;
          const redirectUrls = results.slice(1) as Array<
            (string | { searchKeyword: string })[]
          >;

          return featuredPartners.map((featuredPartner, idx) => ({
            redirectUrl: redirectUrls[idx],
            headingKey: featuredPartner.title,
            descriptionKey: featuredPartner.description,
            ariaLabel: featuredPartner.ariaLabel,
            imageUrl: featuredPartner.image,
            linkKey: 'wl.anz.home_page.get_started',
          }));
        }),
      );
  }
}
